import React, { useState } from 'react';
import Card from '../components/UI/Card';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';
// 🔗 Integración con API REST Defontana (entorno de validación replicado)
// Compras y Ventas provienen de Defontana (Mock Server)
// Gastos de Caja Chica provienen de la plataforma (registrados por trabajadores)
import { getCompras, getVentas, getGastos, useMockApi, handleMockApiError, type Compra, type Venta, type Gasto } from '../services/mockApi';
import { safeFetch } from '../utils/supabaseHelpers';
import { 
  Database,
  Activity,
  RefreshCw,
  Loader2,
  Shield,
  Clock,
  Layers,
  BarChart3,
  TrendingUp,
  TrendingDown,
  PieChart,
  DollarSign,
  FileText,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Zap,
  AlertCircle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  ComposedChart
} from 'recharts';
import { Building2, Users, Tag, Search, Filter, Download, X, Calendar, User } from 'lucide-react';

// Mock de roles de usuario
const mockUserRole = 'Administrador';

export default function ReporteBI() {
  const { hasPermission, user } = useAuth();
  const { executeRequest } = useMockApi();
  
  // Estados para datos centralizados
  const [compras, setCompras] = useState<Compra[]>([]);
  const [ventas, setVentas] = useState<Venta[]>([]);
  const [gastosCajaChica, setGastosCajaChica] = useState<Gasto[]>([]);
  const [cargandoDatos, setCargandoDatos] = useState(false);
  
  const [estadoSincronizacion, setEstadoSincronizacion] = useState({
    estado: 'pendiente',
    ultimaSincronizacion: new Date().toISOString(),
    mensaje: 'Presiona "Actualizar Datos" para cargar la información'
  });
  const [toast, setToast] = useState({ mostrar: false, mensaje: '', tipo: 'success' as 'success' | 'error' | 'info' });
  
  // Estados para indicadores técnicos
  const [indicadoresTecnicos, setIndicadoresTecnicos] = useState({
    tiempoPromedioCarga: 0,
    latenciaDefontana: 0,
    latenciaPlataforma: 0,
    totalRegistros: 0,
    registrosPorFuente: {
      compras: 0,
      ventas: 0,
      gastos: 0
    }
  });
  const [periodoFiltro, setPeriodoFiltro] = useState<'enero' | 'febrero' | 'marzo' | 'abril' | 'mayo' | 'junio' | 'julio' | 'agosto' | 'septiembre' | 'octubre' | 'noviembre' | 'diciembre' | 'año' | 'personalizado'>('octubre');
  const [fechaInicioPersonalizada, setFechaInicioPersonalizada] = useState<string>('');
  const [fechaFinPersonalizada, setFechaFinPersonalizada] = useState<string>('');
  // Variables simplificadas - solo Ventas y Egresos Totales
  const [variablesVisibles, setVariablesVisibles] = useState({
    ventas: true,
    egresosTotales: true
  });
  
  // Estados para tabla de transacciones
  const [filtroTipoTabla, setFiltroTipoTabla] = useState<'todos' | 'venta' | 'compra' | 'gasto'>('todos');
  const [filtroProveedorCliente, setFiltroProveedorCliente] = useState<string>('todos');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todos');
  const [filtroTrabajador, setFiltroTrabajador] = useState<string>('todos');
  const [busquedaTexto, setBusquedaTexto] = useState<string>('');
  const [ordenTabla, setOrdenTabla] = useState<{ campo: string; direccion: 'asc' | 'desc' }>({ campo: 'fecha', direccion: 'desc' });
  
  // Estados para tendencias (simuladas - en producción vendrían de comparación con mes anterior)
  const [tendencias, setTendencias] = useState({
    totalGeneral: { valor: 0, porcentaje: 0, direccion: 'up' as 'up' | 'down' },
    defontana: { valor: 0, porcentaje: 0, direccion: 'up' as 'up' | 'down' },
    cajaChica: { valor: 0, porcentaje: 0, direccion: 'up' as 'up' | 'down' },
    balance: { valor: 0, porcentaje: 0, direccion: 'up' as 'up' | 'down' }
  });

  // 🔄 CARGAR DATOS CENTRALIZADOS - SOLO AL PRESIONAR EL BOTÓN "ACTUALIZAR DATOS"
  // NOTA: Los datos ya NO se cargan automáticamente al montar el componente.
  // Los datos solo se actualizan cuando el usuario presiona el botón "Actualizar Datos".

  const mostrarToast = (mensaje: string, tipo: 'success' | 'error' | 'info' = 'success') => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: '', tipo: 'success' }), 3000);
  };

  // 🔄 FUNCIÓN DE SINCRONIZACIÓN
  const handleSincronizarERP = async () => {
    if (cargandoDatos) {
      console.log('⚠️ Sincronización ya en curso, ignorando solicitud duplicada');
      return;
    }
    
    setEstadoSincronizacion({
      estado: 'sincronizando',
      ultimaSincronizacion: estadoSincronizacion.ultimaSincronizacion,
      mensaje: 'Obteniendo datos desde Defontana y plataforma...'
    });
    setCargandoDatos(true);

    try {
      const fechaInicio = '2025-01-01';
      const fechaFin = '2025-12-31';
      const inicioCarga = Date.now();
      
      console.log('🔄 Iniciando centralización de datos...');
      
      // Ejecutar las 3 consultas en paralelo con timeout individual usando safeFetch
      const [comprasResult, ventasResult, gastosResult] = await Promise.allSettled([
        safeFetch(
          () => executeRequest(() => getCompras({ fechaInicio, fechaFin })),
          { timeout: 30000, retries: 2, errorMessage: 'Timeout al cargar compras' }
        ),
        safeFetch(
          () => executeRequest(() => getVentas({ fechaInicio, fechaFin })),
          { timeout: 30000, retries: 2, errorMessage: 'Timeout al cargar ventas' }
        ),
        safeFetch(
          () => executeRequest(() => getGastos({ fechaInicio, fechaFin })),
          { timeout: 20000, retries: 1, errorMessage: 'Timeout al cargar gastos (20s)' }
        )
      ]);

      // Procesar resultados con fallback seguro
      const comprasArray = comprasResult.status === 'fulfilled' && comprasResult.value.success
        ? (Array.isArray(comprasResult.value.data) ? comprasResult.value.data : [])
        : [];
      
      const ventasArray = ventasResult.status === 'fulfilled' && ventasResult.value.success
        ? (Array.isArray(ventasResult.value.data) ? ventasResult.value.data : [])
        : [];
      
      const gastosArray = gastosResult.status === 'fulfilled' && gastosResult.value.success
        ? (Array.isArray(gastosResult.value.data) ? gastosResult.value.data : [])
        : [];

      // Mostrar errores individuales si los hay
      if (comprasResult.status === 'rejected' || (comprasResult.status === 'fulfilled' && !comprasResult.value.success)) {
        console.error('❌ Error al cargar compras:', comprasResult);
        mostrarToast('Error al cargar compras. Algunos datos pueden estar incompletos.', 'error');
      }
      
      if (ventasResult.status === 'rejected' || (ventasResult.status === 'fulfilled' && !ventasResult.value.success)) {
        console.error('❌ Error al cargar ventas:', ventasResult);
        mostrarToast('Error al cargar ventas. Algunos datos pueden estar incompletos.', 'error');
      }
      
      if (gastosResult.status === 'rejected' || (gastosResult.status === 'fulfilled' && !gastosResult.value.success)) {
        console.error('❌ Error al cargar gastos:', gastosResult);
        mostrarToast('Error al cargar gastos. Algunos datos pueden estar incompletos.', 'error');
      }

      // Actualizar estados
      setCompras(comprasArray);
      setVentas(ventasArray);
      setGastosCajaChica(gastosArray);

      const tiempoTotal = Date.now() - inicioCarga;
      const totalRegistrosCalc = comprasArray.length + ventasArray.length + gastosArray.length;

      console.log('✅ Centralización de datos completada');
      console.log(`   ⏱️ Tiempo total: ${tiempoTotal}ms`);
      console.log(`   📊 Total registros: ${totalRegistrosCalc}`);

      // Actualizar indicadores técnicos
      setIndicadoresTecnicos({
        tiempoPromedioCarga: Math.round(tiempoTotal),
        latenciaDefontana: 0, // Se calcularía si tuviéramos métricas individuales
        latenciaPlataforma: 0,
        totalRegistros: totalRegistrosCalc,
        registrosPorFuente: {
          compras: comprasArray.length,
          ventas: ventasArray.length,
          gastos: gastosArray.length
        }
      });

      setEstadoSincronizacion({
        estado: totalRegistrosCalc > 0 ? 'exitoso' : 'error',
        ultimaSincronizacion: new Date().toISOString(),
        mensaje: totalRegistrosCalc > 0 
          ? 'Datos centralizados actualizados correctamente'
          : 'No se pudieron cargar datos. Verifica la conexión.'
      });

      if (totalRegistrosCalc > 0) {
        mostrarToast('Datos sincronizados exitosamente', 'success');
      } else {
        mostrarToast('No se pudieron cargar datos. Verifica la conexión.', 'error');
      }

    } catch (error) {
      const errorMessage = handleMockApiError(error);
      setEstadoSincronizacion({
        estado: 'error',
        ultimaSincronizacion: estadoSincronizacion.ultimaSincronizacion,
        mensaje: errorMessage
      });
      mostrarToast(errorMessage, 'error');
      console.error('❌ Error al centralizar datos:', error);
    } finally {
      setCargandoDatos(false);
    }
  };

  // Función helper para obtener el mes de una fecha
  const obtenerMesDeFecha = (fecha: string | Date): string => {
    try {
      const date = fecha instanceof Date ? fecha : new Date(fecha);
      return date.toLocaleString('es-ES', { month: 'long' });
    } catch {
      return '';
    }
  };

  // Función helper para normalizar nombre de mes
  const normalizarMes = (mes: string): string => {
    return mes.toLowerCase().trim();
  };

  // Función helper para normalizar fechas (debe estar antes de usarse en filtros)
  const normalizarFecha = (fecha: string | Date | undefined, mes?: string): Date | null => {
    if (!fecha && !mes) return null;
    
    // Si hay mes como string, intentar parsearlo
    if (mes) {
      const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
      const mesIndex = meses.findIndex(m => m.toLowerCase() === mes.toLowerCase());
      if (mesIndex !== -1) {
        const año = new Date().getFullYear();
        if (fecha && typeof fecha === 'string') {
          return new Date(año, mesIndex, parseInt(fecha) || 1);
        }
        return new Date(año, mesIndex, 1);
      }
    }
    
    if (!fecha) return null;
    
    try {
      if (fecha instanceof Date) {
        return isNaN(fecha.getTime()) ? null : fecha;
      }
      
      // Formato DD/MM/YYYY
      if (typeof fecha === 'string' && fecha.includes('/')) {
        const [day, month, year] = fecha.split('/');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      
      // Formato YYYY-MM-DD
      if (typeof fecha === 'string' && fecha.includes('-')) {
        return new Date(fecha);
      }
      
      // Intentar parsear como ISO
      return new Date(fecha);
    } catch {
      return null;
    }
  };

  // Función para obtener rango de fechas según período seleccionado
  const obtenerRangoPeriodo = () => {
    const fechaActual = new Date();
    const añoActual = fechaActual.getFullYear();
    
    // Mapeo de meses en español a índices (0-11)
    const mesesMap: Record<string, number> = {
      'enero': 0,
      'febrero': 1,
      'marzo': 2,
      'abril': 3,
      'mayo': 4,
      'junio': 5,
      'julio': 6,
      'agosto': 7,
      'septiembre': 8,
      'octubre': 9,
      'noviembre': 10,
      'diciembre': 11
    };
    
    if (periodoFiltro === 'personalizado') {
      // Rango personalizado desde calendario
      if (fechaInicioPersonalizada && fechaFinPersonalizada) {
        // Crear fechas en zona horaria local para evitar problemas de UTC
        // Parsear la fecha manualmente para evitar problemas de zona horaria
        const inicioParts = fechaInicioPersonalizada.split('-');
        const finParts = fechaFinPersonalizada.split('-');
        
        const inicio = new Date(
          parseInt(inicioParts[0]),      // año
          parseInt(inicioParts[1]) - 1,  // mes (0-indexed)
          parseInt(inicioParts[2]),      // día
          0,                             // hora
          0,                             // minutos
          0,                             // segundos
          0                              // milisegundos
        );
        
        const fin = new Date(
          parseInt(finParts[0]),         // año
          parseInt(finParts[1]) - 1,      // mes (0-indexed)
          parseInt(finParts[2]),          // día
          23,                            // hora (final del día)
          59,                            // minutos
          59,                            // segundos
          999                            // milisegundos
        );
        
        return { inicio, fin };
      }
      // Si no hay fechas seleccionadas, usar todo el año actual
      return {
        inicio: new Date(añoActual, 0, 1),
        fin: new Date(añoActual, 11, 31, 23, 59, 59, 999)
      };
    } else if (mesesMap[periodoFiltro] !== undefined) {
      // Mes específico del año actual
      const mesIndex = mesesMap[periodoFiltro];
      const inicio = new Date(añoActual, mesIndex, 1); // Primer día del mes
      const fin = new Date(añoActual, mesIndex + 1, 0, 23, 59, 59, 999); // Último día del mes
      return { inicio, fin };
    } else if (periodoFiltro === 'año') {
      // Año actual completo
      return {
        inicio: new Date(añoActual, 0, 1), // 1 de enero del año actual
        fin: new Date(añoActual, 11, 31, 23, 59, 59, 999) // 31 de diciembre del año actual, fin del día
      };
    } else {
      // Último año completo (fallback)
      const año = fechaActual.getFullYear() - 1;
      return {
        inicio: new Date(año, 0, 1), // 1 de enero
        fin: new Date(año, 11, 31, 23, 59, 59, 999) // 31 de diciembre, fin del día
      };
    }
  };

  // Filtrar datos según período seleccionado (afecta a TODOS los componentes)
  const rangoPeriodo = obtenerRangoPeriodo();
  
  const comprasFiltradas = compras.filter(c => {
    const fecha = normalizarFecha(c.fecha, c.mes);
    if (!fecha) return false;
    return fecha >= rangoPeriodo.inicio && fecha <= rangoPeriodo.fin;
  });
  
  const ventasFiltradas = ventas.filter(v => {
    const fecha = normalizarFecha(v.fecha, v.mes);
    if (!fecha) return false;
    return fecha >= rangoPeriodo.inicio && fecha <= rangoPeriodo.fin;
  });
  
  // ✅ Función helper para excluir gastos rechazados Y pendientes
  // Solo contar gastos aprobados explícitamente (no pendientes ni rechazados)
  // Misma lógica que en CajaChica.tsx para mantener consistencia
  const excluirGastosRechazados = (gastos: any[]) => {
    return gastos.filter((gasto: any) => {
      const estadoAlerta = gasto.estado_alerta;
      // Excluir: rechazados Y pendientes
      // Incluir: solo aprobados o sin estado_alerta (compatibilidad con gastos antiguos)
      if (estadoAlerta === 'rechazado' || estadoAlerta === 'pendiente') {
        return false;
      }
      // Incluir solo si es 'aprobado' o null/undefined (gastos antiguos sin estado)
      return estadoAlerta === 'aprobado' || !estadoAlerta;
    });
  };

  // Filtrar gastos por período
  const gastosFiltradosPorPeriodo = gastosCajaChica.filter(g => {
    const fecha = normalizarFecha(g.fecha);
    if (!fecha) return false;
    return fecha >= rangoPeriodo.inicio && fecha <= rangoPeriodo.fin;
  });

  // ✅ Aplicar filtro de estado: solo gastos aprobados (para métricas consolidadas)
  const gastosFiltrados = excluirGastosRechazados(gastosFiltradosPorPeriodo);
  
  // ✅ DEBUG: Log para diagnosticar por qué no aparecen gastos
  React.useEffect(() => {
    console.log('🔍 [ReporteBI] Diagnóstico de gastos:', {
      totalGastosCajaChica: gastosCajaChica.length,
      gastosFiltradosPorPeriodo: gastosFiltradosPorPeriodo.length,
      gastosFiltrados: gastosFiltrados.length,
      rangoPeriodo: {
        inicio: rangoPeriodo.inicio.toISOString(),
        fin: rangoPeriodo.fin.toISOString()
      },
      estadosGastos: gastosCajaChica.map((g: any) => ({
        id: g.id,
        fecha: g.fecha,
        estado_alerta: g.estado_alerta,
        monto: g.monto
      }))
    });
  }, [gastosCajaChica, gastosFiltradosPorPeriodo, gastosFiltrados, rangoPeriodo]);

  // ============================================
  // CÁLCULOS CONTABLES CORRECTOS
  // ============================================
  // Ingresos
  const totalVentas = ventasFiltradas.reduce((sum, v) => sum + (v.monto || 0), 0);
  
  // Costos directos (facturas de proveedores)
  const totalCompras = comprasFiltradas.reduce((sum, c) => sum + (c.monto || 0), 0);
  
  // Gastos operacionales menores (caja chica)
  const totalGastos = gastosFiltrados.reduce((sum, g) => sum + (g.monto || 0), 0);
  
  // Egresos Totales = Compras + Gastos (NO se comparan con ventas de forma independiente)
  const totalEgresos = totalCompras + totalGastos;
  
  // Balance Neto = Ventas - Egresos Totales
  const balanceNeto = totalVentas - totalEgresos;
  
  // Margen Neto (%) = Balance Neto / Ventas
  const margenNeto = totalVentas > 0 ? (balanceNeto / totalVentas) * 100 : 0;
  
  // Margen Bruto (%) = (Ventas - Compras) / Ventas
  const margenBruto = totalVentas > 0 ? ((totalVentas - totalCompras) / totalVentas) * 100 : 0;
  
  // Total general para estadísticas (solo informativo)
  const totalGeneral = totalVentas + totalEgresos;
  const totalRegistros = comprasFiltradas.length + ventasFiltradas.length + gastosFiltrados.length;

  // ============================================
  // DATOS PARA GRÁFICOS SECUNDARIOS (hooks después de cálculos, antes de return condicional)
  // ============================================
  
  // Gráfico 1: Compras por Proveedor (TOP 3)
  const datosComprasPorProveedor = React.useMemo(() => {
    const proveedoresMap = new Map<string, { total: number; facturas: number; nombreOriginal: string }>();
    
    comprasFiltradas.forEach(compra => {
      // Normalizar nombre del proveedor (trim y usar nombre original para display)
      const proveedorRaw = compra.proveedor || 'Sin proveedor';
      const proveedorNormalizado = proveedorRaw.trim();
      const proveedorKey = proveedorNormalizado.toLowerCase();
      
      const current = proveedoresMap.get(proveedorKey) || { 
        total: 0, 
        facturas: 0, 
        nombreOriginal: proveedorNormalizado 
      };
      current.total += compra.monto || 0;
      current.facturas += 1;
      // Mantener el nombre original más común (el que aparece más veces)
      if (current.facturas <= 1) {
        current.nombreOriginal = proveedorNormalizado;
      }
      proveedoresMap.set(proveedorKey, current);
    });
    
    const todosProveedores = Array.from(proveedoresMap.values())
      .map(datos => ({
        proveedor: datos.nombreOriginal,
        total: Math.round(datos.total),
        facturas: datos.facturas
      }))
      .filter(p => p.total > 0) // Filtrar proveedores con total = 0
      .sort((a, b) => b.total - a.total);
    
    // Log para debug
    console.log('📊 [ReporteBI] Proveedores encontrados:', {
      totalCompras: comprasFiltradas.length,
      totalProveedores: todosProveedores.length,
      proveedores: todosProveedores.map(p => ({ nombre: p.proveedor, total: p.total, facturas: p.facturas })),
      top3: todosProveedores.slice(0, 3).map(p => ({ nombre: p.proveedor, total: p.total, facturas: p.facturas }))
    });
    
    // Asegurar que siempre se retornen hasta 3 proveedores
    return todosProveedores.slice(0, 3);
  }, [comprasFiltradas]);
  
  
  // Nota: El gráfico de "Gastos por Categoría" fue eliminado porque ya existe en la sección de Caja Chica
  
  // Colores unificados para todos los gráficos
  const COLORES_UNIFICADOS = {
    ventas: '#3B82F6',      // Azul
    compras: '#F97316',     // Naranja
    gastos: '#F59E0B',      // Amarillo
    egresosTotales: '#EF4444', // Rojo
    balance: '#10B981',      // Verde
    margenes: '#8B5CF6'      // Morado
  };
  
  // ============================================
  // FUNCIONES AUXILIARES (deben estar antes de los hooks que las usan)
  // ============================================
  
  // Nota: normalizarFecha ya está definida arriba (línea 423) antes de los filtros

  // Función para obtener período según filtro
  const obtenerPeriodo = (fecha: Date | null, periodo: 'mes' | 'año'): string => {
    if (!fecha) return 'Sin fecha';
    
    const año = fecha.getFullYear();
    const mes = fecha.getMonth() + 1;
    
    if (periodo === 'año') {
      return año.toString();
    } else {
      // mes
      const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      return `${meses[mes - 1]} ${año}`;
    }
  };
  
  // ============================================
  // DATOS PARA TABLA DE TRANSACCIONES
  // ============================================
  
  // Combinar todas las transacciones en un solo array
  const todasLasTransacciones = React.useMemo(() => {
    const transacciones: Array<{
      id: string;
      fecha: Date | null;
      tipo: 'venta' | 'compra' | 'gasto';
      monto: number;
      descripcion: string;
      categoria?: string;
      proveedorCliente: string;
      trabajador?: string;
      origen: 'Defontana' | 'Caja Chica';
      numeroDocumento?: string;
    }> = [];
    
    // Agregar compras
    comprasFiltradas.forEach(compra => {
      const fecha = normalizarFecha(compra.fecha, compra.mes);
      if (!fecha) return; // Eliminar sin fecha
      transacciones.push({
        id: `compra-${compra.numero_factura || compra.id || Math.random()}`,
        fecha,
        tipo: 'compra',
        monto: compra.monto || 0,
        descripcion: compra.descripcion || '',
        proveedorCliente: compra.proveedor || 'Sin proveedor',
        origen: 'Defontana',
        numeroDocumento: compra.numero_factura
      });
    });
    
    // Agregar ventas
    ventasFiltradas.forEach(venta => {
      const fecha = normalizarFecha(venta.fecha);
      if (!fecha) return; // Eliminar sin fecha
      transacciones.push({
        id: `venta-${venta.numero_boleta || venta.id || Math.random()}`,
        fecha,
        tipo: 'venta',
        monto: venta.monto || 0,
        descripcion: venta.descripcion || venta.categoria || '',
        proveedorCliente: venta.cliente || 'Sin cliente',
        origen: 'Defontana',
        numeroDocumento: venta.numero_boleta
      });
    });
    
    // Agregar gastos
    gastosFiltrados.forEach(gasto => {
      const fecha = normalizarFecha(gasto.fecha);
      if (!fecha) return; // Eliminar sin fecha
      transacciones.push({
        id: `gasto-${gasto.id || Math.random()}`,
        fecha,
        tipo: 'gasto',
        monto: gasto.monto || 0,
        descripcion: gasto.descripcion || '',
        categoria: gasto.categoria || 'Sin categoría',
        proveedorCliente: gasto.trabajador || 'Sin trabajador',
        trabajador: gasto.trabajador,
        origen: 'Caja Chica'
      });
    });
    
    return transacciones;
  }, [comprasFiltradas, ventasFiltradas, gastosFiltrados]);
  
  // Filtrar y ordenar transacciones
  const transaccionesFiltradas = React.useMemo(() => {
    let filtradas = todasLasTransacciones;
    
    // Filtro por tipo
    if (filtroTipoTabla !== 'todos') {
      filtradas = filtradas.filter(t => t.tipo === filtroTipoTabla);
    }
    
    // Filtro por proveedor/cliente
    if (filtroProveedorCliente !== 'todos') {
      filtradas = filtradas.filter(t => t.proveedorCliente === filtroProveedorCliente);
    }
    
    // Filtro por categoría
    if (filtroCategoria !== 'todos') {
      filtradas = filtradas.filter(t => t.categoria === filtroCategoria);
    }
    
    // Filtro por trabajador
    if (filtroTrabajador !== 'todos') {
      filtradas = filtradas.filter(t => t.trabajador === filtroTrabajador);
    }
    
    // Búsqueda por texto
    if (busquedaTexto) {
      const busqueda = busquedaTexto.toLowerCase();
      filtradas = filtradas.filter(t => 
        t.descripcion.toLowerCase().includes(busqueda) ||
        t.proveedorCliente.toLowerCase().includes(busqueda) ||
        (t.categoria && t.categoria.toLowerCase().includes(busqueda)) ||
        (t.numeroDocumento && t.numeroDocumento.toLowerCase().includes(busqueda))
      );
    }
    
    // Ordenar
    filtradas.sort((a, b) => {
      const aVal = ordenTabla.campo === 'fecha' 
        ? (a.fecha?.getTime() || 0)
        : ordenTabla.campo === 'monto'
        ? a.monto
        : ordenTabla.campo === 'tipo'
        ? a.tipo
        : 0;
      
      const bVal = ordenTabla.campo === 'fecha'
        ? (b.fecha?.getTime() || 0)
        : ordenTabla.campo === 'monto'
        ? b.monto
        : ordenTabla.campo === 'tipo'
        ? b.tipo
        : 0;
      
      if (ordenTabla.direccion === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtradas;
  }, [todasLasTransacciones, filtroTipoTabla, filtroProveedorCliente, filtroCategoria, filtroTrabajador, busquedaTexto, ordenTabla]);
  
  // Obtener opciones únicas para filtros
  const opcionesProveedoresClientes = React.useMemo(() => {
    const set = new Set<string>();
    todasLasTransacciones.forEach(t => set.add(t.proveedorCliente));
    return Array.from(set).sort();
  }, [todasLasTransacciones]);
  
  const opcionesCategorias = React.useMemo(() => {
    const set = new Set<string>();
    todasLasTransacciones.forEach(t => {
      if (t.categoria) set.add(t.categoria);
    });
    return Array.from(set).sort();
  }, [todasLasTransacciones]);
  
  const opcionesTrabajadores = React.useMemo(() => {
    const set = new Set<string>();
    todasLasTransacciones.forEach(t => {
      if (t.trabajador) set.add(t.trabajador);
    });
    return Array.from(set).sort();
  }, [todasLasTransacciones]);
  
  // Totales segmentados
  const totalesSegmentados = React.useMemo(() => {
    return {
      ventas: transaccionesFiltradas.filter(t => t.tipo === 'venta').reduce((sum, t) => sum + t.monto, 0),
      compras: transaccionesFiltradas.filter(t => t.tipo === 'compra').reduce((sum, t) => sum + t.monto, 0),
      gastos: transaccionesFiltradas.filter(t => t.tipo === 'gasto').reduce((sum, t) => sum + t.monto, 0)
    };
  }, [transaccionesFiltradas]);
  
  // Función para exportar a CSV
  const exportarCSV = () => {
    const headers = ['Fecha', 'Tipo', 'Monto', 'Descripción', 'Categoría', 'Proveedor/Cliente', 'Trabajador', 'Origen', 'N° Documento'];
    const rows = transaccionesFiltradas.map(t => [
      t.fecha ? t.fecha.toLocaleDateString('es-CL') : '',
      t.tipo.charAt(0).toUpperCase() + t.tipo.slice(1),
      t.monto.toString(),
      t.descripcion,
      t.categoria || '',
      t.proveedorCliente,
      t.trabajador || '',
      t.origen,
      t.numeroDocumento || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transacciones_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Función para cambiar orden
  const cambiarOrden = (campo: string) => {
    setOrdenTabla(prev => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'desc' ? 'asc' : 'desc'
    }));
  };
  
  // ============================================
  // CÁLCULOS ADICIONALES PARA KPIs
  // ============================================
  
  // Función para obtener datos del período anterior para comparación
  const obtenerDatosPeriodoAnterior = () => {
    // Calcular variaciones comparando con el período anterior según el filtro seleccionado
    const fechaActual = new Date();
    let fechaInicioAnterior: Date;
    let fechaFinAnterior: Date;
    
    // Verificar si es un mes específico
    const mesesMap: Record<string, number> = {
      'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3, 'mayo': 4, 'junio': 5,
      'julio': 6, 'agosto': 7, 'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
    };
    
    if (mesesMap[periodoFiltro] !== undefined) {
      // Mes anterior al mes seleccionado
      const mesIndex = mesesMap[periodoFiltro];
      const añoActual = fechaActual.getFullYear();
      
      if (mesIndex === 0) {
        // Si es enero, el mes anterior es diciembre del año anterior
        fechaInicioAnterior = new Date(añoActual - 1, 11, 1);
        fechaFinAnterior = new Date(añoActual - 1, 11, 31, 23, 59, 59, 999);
      } else {
        fechaInicioAnterior = new Date(añoActual, mesIndex - 1, 1);
        fechaFinAnterior = new Date(añoActual, mesIndex, 0, 23, 59, 59, 999);
      }
    } else {
      // Año anterior
      fechaInicioAnterior = new Date(fechaActual.getFullYear() - 1, 0, 1);
      fechaFinAnterior = new Date(fechaActual.getFullYear() - 1, 11, 31);
    }
    
    // Filtrar datos del período anterior
    const ventasAnteriores = ventas.filter(v => {
      const fecha = normalizarFecha(v.fecha);
      if (!fecha) return false;
      return fecha >= fechaInicioAnterior && fecha <= fechaFinAnterior;
    });
    
    const comprasAnteriores = compras.filter(c => {
      const fecha = normalizarFecha(c.fecha, c.mes);
      if (!fecha) return false;
      return fecha >= fechaInicioAnterior && fecha <= fechaFinAnterior;
    });
    
    const gastosAnteriores = gastosCajaChica.filter(g => {
      const fecha = normalizarFecha(g.fecha);
      if (!fecha) return false;
      return fecha >= fechaInicioAnterior && fecha <= fechaFinAnterior;
    });
    
    return {
      ventas: ventasAnteriores.reduce((sum, v) => sum + (v.monto || 0), 0),
      compras: comprasAnteriores.reduce((sum, c) => sum + (c.monto || 0), 0),
      gastos: gastosAnteriores.reduce((sum, g) => sum + (g.monto || 0), 0)
    };
  };
  
  // Calcular variaciones
  const datosAnteriores = obtenerDatosPeriodoAnterior();
  const variacionVentas = datosAnteriores.ventas > 0 
    ? ((totalVentas - datosAnteriores.ventas) / datosAnteriores.ventas) * 100 
    : (totalVentas > 0 ? 100 : 0);
  const variacionCompras = datosAnteriores.compras > 0 
    ? ((totalCompras - datosAnteriores.compras) / datosAnteriores.compras) * 100 
    : (totalCompras > 0 ? 100 : 0);
  const variacionGastos = datosAnteriores.gastos > 0 
    ? ((totalGastos - datosAnteriores.gastos) / datosAnteriores.gastos) * 100 
    : (totalGastos > 0 ? 100 : 0);
  
  // Tickets promedios
  const ticketPromedioVenta = ventasFiltradas.length > 0 
    ? totalVentas / ventasFiltradas.length 
    : 0;
  const ticketPromedioCompra = comprasFiltradas.length > 0 
    ? totalCompras / comprasFiltradas.length 
    : 0;
  
  // Cantidad de transacciones
  const cantidadVentas = ventasFiltradas.length;
  const cantidadCompras = comprasFiltradas.length;
  const cantidadGastos = gastosFiltrados.length;

  // Función para formatear fechas
  const formatearFecha = (fecha?: string, mes?: string): string => {
    if (fecha) {
      if (fecha.includes('/')) return fecha;
      if (fecha.includes('-')) {
        const [year, month, day] = fecha.split('-');
        return `${day}/${month}/${year}`;
      }
      try {
        const date = new Date(fecha);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch {
        return fecha;
      }
    }
    if (mes) return mes;
    return '-';
  };

  // Preparar datos consolidados para gráficos (usando datos filtrados)
  const datosConsolidados = {
    compras: comprasFiltradas,
    ventas: ventasFiltradas,
    gastos: gastosFiltrados,
    totalCompras,
    totalVentas,
    totalGastos,
    totalGeneral
  };

  // Preparar datos para el gráfico maestro - Agrupación según período seleccionado
  // Usar useMemo para optimizar y asegurar actualización dinámica
  const datosGrafico = React.useMemo(() => {
    // Obtener el rango de fechas del período para debug
    const rango = obtenerRangoPeriodo();
    console.log('📅 Rango de período:', {
      inicio: rango.inicio.toISOString(),
      fin: rango.fin.toISOString(),
      inicioLocal: rango.inicio.toLocaleString('es-CL'),
      finLocal: rango.fin.toLocaleString('es-CL'),
      periodoFiltro,
      fechaInicioPersonalizada,
      fechaFinPersonalizada
    });
    
    // Agrupar datos filtrados según el período seleccionado
    const datosPorMes = new Map<string, {
      ventas: number;
      compras: number;
      gastos: number;
      cantidadVentas: number;
      cantidadCompras: number;
      cantidadGastos: number;
      fechaInicio: Date | null;
      proveedores: Map<string, number>;
      clientes: Map<string, number>;
    }>();
    
    // Procesar compras filtradas - Eliminar valores sin fecha
    console.log('🛒 Compras filtradas:', comprasFiltradas.length);
    comprasFiltradas.forEach(compra => {
      const fecha = normalizarFecha(compra.fecha, compra.mes);
      if (!fecha) return; // Eliminar valores sin fecha
      
      const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
      const mesNombre = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'][fecha.getMonth()];
      
      // Debug: verificar si la fecha está dentro del rango
      const dentroDelRango = fecha >= rango.inicio && fecha <= rango.fin;
      if (!dentroDelRango) {
        console.warn('⚠️ Compra fuera del rango:', {
          fecha: fecha.toLocaleString('es-CL'),
          mes: mesNombre,
          mesKey,
          rangoInicio: rango.inicio.toLocaleString('es-CL'),
          rangoFin: rango.fin.toLocaleString('es-CL')
        });
        return;
      }
      
      const current = datosPorMes.get(mesKey) || {
        ventas: 0,
        compras: 0,
        gastos: 0,
        cantidadVentas: 0,
        cantidadCompras: 0,
        cantidadGastos: 0,
        fechaInicio: fecha,
        proveedores: new Map<string, number>(),
        clientes: new Map<string, number>()
      };
      current.compras += compra.monto || 0;
      current.cantidadCompras += 1;
      // Agregar a proveedores
      const proveedor = compra.proveedor || 'Sin proveedor';
      current.proveedores.set(proveedor, (current.proveedores.get(proveedor) || 0) + (compra.monto || 0));
      datosPorMes.set(mesKey, current);
    });
    
    // Procesar ventas filtradas - Eliminar valores sin fecha
    console.log('🔍 Procesando ventas:', ventasFiltradas.length, 'registros (filtradas por período)');
    let ventasProcesadas = 0;
    let ventasSinFecha = 0;
    let ventasFueraRango = 0;
    
    ventasFiltradas.forEach(venta => {
      // Intentar normalizar fecha, usando mes como fallback si fecha no está disponible
      const fecha = normalizarFecha(venta.fecha, venta.mes);
      if (!fecha) {
        ventasSinFecha++;
        console.warn('⚠️ Venta sin fecha válida:', {
          id: venta.id,
          numero_boleta: venta.numero_boleta,
          fecha: venta.fecha,
          mes: venta.mes,
          cliente: venta.cliente,
          monto: venta.monto
        });
        return; // Eliminar valores sin fecha
      }
      
      const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
      const mesNombre = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'][fecha.getMonth()];
      
      // Debug: verificar si la fecha está dentro del rango (aunque ya fue filtrada)
      const dentroDelRango = fecha >= rango.inicio && fecha <= rango.fin;
      if (!dentroDelRango) {
        ventasFueraRango++;
        console.warn('⚠️ Venta fuera del rango (aunque pasó el filtro):', {
          fecha: fecha.toLocaleString('es-CL'),
          mes: mesNombre,
          mesKey,
          rangoInicio: rango.inicio.toLocaleString('es-CL'),
          rangoFin: rango.fin.toLocaleString('es-CL')
        });
        return;
      }
      
      const current = datosPorMes.get(mesKey) || {
        ventas: 0,
        compras: 0,
        gastos: 0,
        cantidadVentas: 0,
        cantidadCompras: 0,
        cantidadGastos: 0,
        fechaInicio: fecha,
        proveedores: new Map<string, number>(),
        clientes: new Map<string, number>()
      };
      current.ventas += venta.monto || 0;
      current.cantidadVentas += 1;
      ventasProcesadas++;
      // Agregar a clientes
      const cliente = venta.cliente || 'Sin cliente';
      current.clientes.set(cliente, (current.clientes.get(cliente) || 0) + (venta.monto || 0));
      datosPorMes.set(mesKey, current);
    });
    
    console.log(`✅ Ventas procesadas: ${ventasProcesadas}, Sin fecha: ${ventasSinFecha}, Fuera de rango: ${ventasFueraRango}`);
    console.log('📊 Meses encontrados en datosPorMes:', Array.from(datosPorMes.keys()));
    
    // Procesar gastos filtrados - Eliminar valores sin fecha
    console.log('💰 Gastos filtrados:', gastosFiltrados.length);
    gastosFiltrados.forEach(gasto => {
      const fecha = normalizarFecha(gasto.fecha);
      if (!fecha) return; // Eliminar valores sin fecha
      
      const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
      const mesNombre = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'][fecha.getMonth()];
      
      // Debug: verificar si la fecha está dentro del rango
      const dentroDelRango = fecha >= rango.inicio && fecha <= rango.fin;
      if (!dentroDelRango) {
        console.warn('⚠️ Gasto fuera del rango:', {
          fecha: fecha.toLocaleString('es-CL'),
          mes: mesNombre,
          mesKey,
          rangoInicio: rango.inicio.toLocaleString('es-CL'),
          rangoFin: rango.fin.toLocaleString('es-CL')
        });
        return;
      }
      
      const current = datosPorMes.get(mesKey) || {
        ventas: 0,
        compras: 0,
        gastos: 0,
        cantidadVentas: 0,
        cantidadCompras: 0,
        cantidadGastos: 0,
        fechaInicio: fecha,
        proveedores: new Map<string, number>(),
        clientes: new Map<string, number>()
      };
      current.gastos += gasto.monto || 0;
      current.cantidadGastos += 1;
      datosPorMes.set(mesKey, current);
    });
    
    // Convertir a array y calcular métricas
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    let egresosAcumulados = 0;
    
    const datos = Array.from(datosPorMes.entries())
      .map(([mesKey, valores]) => {
        const [año, mes] = mesKey.split('-');
        const mesIndex = parseInt(mes) - 1;
        const nombreMes = meses[mesIndex];
        const categoria = `${nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1)} ${año}`;
        
        // Egresos Totales = Compras + Gastos
        const egresos = valores.compras + valores.gastos;
        egresosAcumulados += egresos;
        
        // Balance Neto = Ventas - Egresos Totales
        const balance = valores.ventas - egresos;
        
        // Márgenes
        const margenNeto = valores.ventas > 0 ? (balance / valores.ventas) * 100 : 0;
        const margenBruto = valores.ventas > 0 ? ((valores.ventas - valores.compras) / valores.ventas) * 100 : 0;
        
        // Top Proveedor del mes
        const topProveedor = Array.from(valores.proveedores.entries())
          .sort((a, b) => b[1] - a[1])[0] || ['N/A', 0];
        
        // Top Cliente del mes
        const topCliente = Array.from(valores.clientes.entries())
          .sort((a, b) => b[1] - a[1])[0] || ['N/A', 0];
        
        return {
          categoria,
          mesKey,
          año: parseInt(año),
          mes: mesIndex,
          ventas: Math.round(valores.ventas),
          compras: Math.round(valores.compras),
          gastos: Math.round(valores.gastos),
          egresos: Math.round(egresos),
          egresosAcumulados: Math.round(egresosAcumulados),
          balance: Math.round(balance),
          margenNeto: Math.round(margenNeto * 10) / 10,
          margenBruto: Math.round(margenBruto * 10) / 10,
          cantidadVentas: valores.cantidadVentas,
          cantidadCompras: valores.cantidadCompras,
          cantidadGastos: valores.cantidadGastos,
          fechaInicio: valores.fechaInicio,
          topProveedor: topProveedor[0],
          topProveedorMonto: Math.round(topProveedor[1]),
          topCliente: topCliente[0],
          topClienteMonto: Math.round(topCliente[1])
        };
      })
      .sort((a, b) => {
        // Ordenar cronológicamente
        if (a.año !== b.año) return a.año - b.año;
        return a.mes - b.mes;
      });
    
    // Calcular variaciones mensuales
    const datosConVariaciones = datos.map((dato, index) => {
      if (index === 0) {
        return {
          ...dato,
          variacionVentas: 0,
          variacionEgresos: 0
        };
      }
      
      const datoAnterior = datos[index - 1];
      const variacionVentas = datoAnterior.ventas > 0
        ? ((dato.ventas - datoAnterior.ventas) / datoAnterior.ventas) * 100
        : (dato.ventas > 0 ? 100 : 0);
      const variacionEgresos = datoAnterior.egresos > 0
        ? ((dato.egresos - datoAnterior.egresos) / datoAnterior.egresos) * 100
        : (dato.egresos > 0 ? 100 : 0);
      
      return {
        ...dato,
        variacionVentas: Math.round(variacionVentas * 10) / 10,
        variacionEgresos: Math.round(variacionEgresos * 10) / 10
      };
    });
    
    // Si no hay datos, retornar un placeholder
    if (datosConVariaciones.length === 0) {
      return [{
        categoria: 'Sin datos',
        mesKey: '',
        año: 0,
        mes: 0,
        ventas: 0,
        compras: 0,
        gastos: 0,
        egresos: 0,
        egresosAcumulados: 0,
        balance: 0,
        margenNeto: 0,
        margenBruto: 0,
        variacionVentas: 0,
        variacionEgresos: 0,
        cantidadVentas: 0,
        cantidadCompras: 0,
        cantidadGastos: 0,
        fechaInicio: null,
        topProveedor: 'N/A',
        topProveedorMonto: 0,
        topCliente: 'N/A',
        topClienteMonto: 0
      }];
    }
    
    return datosConVariaciones;
  }, [comprasFiltradas, ventasFiltradas, gastosFiltrados, periodoFiltro, fechaInicioPersonalizada, fechaFinPersonalizada]); // Agregar dependencias del período

  // ============================================
  // VERIFICACIÓN DE PERMISOS (después de todos los hooks)
  // ============================================
  if (!hasPermission('view_reportes_bi')) {
    return <AccessDenied 
      message="No tienes permisos para acceder a los reportes de Business Intelligence."
      requiredRole="Contadora o Administrador"
      currentRole={user?.rol}
    />;
  }

  return (
    <div className="p-3 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {/* Toast de notificaciones */}
      {toast.mostrar && (
        <div className={`fixed top-4 right-4 z-50 px-4 sm:px-6 py-3 rounded-lg shadow-xl text-sm sm:text-base ${
          toast.tipo === 'success' ? 'bg-green-500 text-white' :
          toast.tipo === 'error' ? 'bg-red-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          {toast.mensaje}
        </div>
      )}

      {/* ============================================ */}
      {/* HEADER EJECUTIVO */}
      {/* ============================================ */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Reporte de Inteligencia de Negocios</h1>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600">
              Dashboard Financiero — Ingresos vs Egresos | Datos Centralizados
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <div className="text-left sm:text-right">
              <p className="text-xs text-gray-500">Última actualización</p>
              <p className="text-xs sm:text-sm font-semibold text-gray-700">
                {new Date(estadoSincronizacion.ultimaSincronizacion).toLocaleString()}
              </p>
            </div>
            <button
              onClick={handleSincronizarERP}
              disabled={cargandoDatos}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all flex items-center gap-2 shadow-lg text-sm sm:text-base w-full sm:w-auto justify-center ${
                cargandoDatos
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-600 to-orange-500 text-white hover:from-orange-700 hover:to-orange-600'
              }`}
            >
              {cargandoDatos ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  <span className="hidden sm:inline">Sincronizando...</span>
                  <span className="sm:hidden">Sincronizando</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Actualizar Datos</span>
                  <span className="sm:hidden">Actualizar</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Indicadores de estado */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${
              estadoSincronizacion.estado === 'exitoso' ? 'bg-green-500' :
              estadoSincronizacion.estado === 'sincronizando' ? 'bg-amber-500 animate-pulse' :
              estadoSincronizacion.estado === 'pendiente' ? 'bg-gray-400' :
              'bg-red-500'
            }`}></div>
            <span className="text-gray-700 font-medium text-xs sm:text-sm">{estadoSincronizacion.mensaje}</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-gray-500" />
            <span className="text-gray-600 text-xs sm:text-sm">Rol: {mockUserRole}</span>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* FILTRO DE PERÍODO UNIFICADO - ACTUALIZA TODOS LOS COMPONENTES */}
      {/* ============================================ */}
      <Card className="mb-4 sm:mb-6 shadow-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 via-blue-50 to-white">
        <div className="p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4 w-full lg:w-auto">
              <div className="p-2 sm:p-3 bg-indigo-100 rounded-xl shadow-sm flex-shrink-0">
                <Calendar className="w-5 h-5 sm:w-7 sm:h-7 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Filtro de Período</h3>
                <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                    <RefreshCw className="w-3 h-3" />
                    <span className="hidden sm:inline">Actualiza todos los componentes</span>
                    <span className="sm:hidden">Actualiza todo</span>
                  </span>
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-4 w-full lg:w-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full">
                <label htmlFor="periodo-selector-global" className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                  Período:
              </label>
            <select
                  id="periodo-selector-global"
                  value={periodoFiltro}
                  onChange={(e) => {
                    const nuevoPeriodo = e.target.value as 'enero' | 'febrero' | 'marzo' | 'abril' | 'mayo' | 'junio' | 'julio' | 'agosto' | 'septiembre' | 'octubre' | 'noviembre' | 'diciembre' | 'año' | 'personalizado';
                    setPeriodoFiltro(nuevoPeriodo);
                    // Limpiar fechas personalizadas si se cambia a otro período
                    if (nuevoPeriodo !== 'personalizado') {
                      setFechaInicioPersonalizada('');
                      setFechaFinPersonalizada('');
                    }
                  }}
                  className="px-4 sm:px-5 py-2 sm:py-3 border-2 border-indigo-300 rounded-lg bg-white text-gray-900 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-md hover:shadow-lg w-full sm:min-w-[250px]"
                >
                  <optgroup label="Meses del Año">
                    <option value="enero">Enero</option>
                    <option value="febrero">Febrero</option>
                    <option value="marzo">Marzo</option>
                    <option value="abril">Abril</option>
                    <option value="mayo">Mayo</option>
                    <option value="junio">Junio</option>
                    <option value="julio">Julio</option>
                    <option value="agosto">Agosto</option>
                    <option value="septiembre">Septiembre</option>
                    <option value="octubre">Octubre</option>
                    <option value="noviembre">Noviembre</option>
                    <option value="diciembre">Diciembre</option>
                  </optgroup>
                  <optgroup label="Períodos">
                    <option value="año">Anual (Último año completo)</option>
                    <option value="personalizado">Rango personalizado (Calendario)</option>
                  </optgroup>
            </select>
          </div>
              
              {/* Calendario para rango personalizado */}
              {periodoFiltro === 'personalizado' && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                    <label htmlFor="fecha-inicio" className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Desde:
                    </label>
                    <input
                      id="fecha-inicio"
                      type="date"
                      value={fechaInicioPersonalizada}
                      onChange={(e) => setFechaInicioPersonalizada(e.target.value)}
                      max={fechaFinPersonalizada || undefined}
                      className="px-3 py-2 border-2 border-indigo-300 rounded-lg bg-white text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                    <label htmlFor="fecha-fin" className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Hasta:
                    </label>
                    <input
                      id="fecha-fin"
                      type="date"
                      value={fechaFinPersonalizada}
                      onChange={(e) => setFechaFinPersonalizada(e.target.value)}
                      min={fechaInicioPersonalizada || undefined}
                      max={new Date().toISOString().split('T')[0]}
                      className="px-3 py-2 border-2 border-indigo-300 rounded-lg bg-white text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm"
                    />
                  </div>
                  {fechaInicioPersonalizada && fechaFinPersonalizada && (
                    <div className="text-xs text-indigo-700 font-medium">
                      {new Date(fechaInicioPersonalizada).toLocaleDateString('es-CL')} - {new Date(fechaFinPersonalizada).toLocaleDateString('es-CL')}
            </div>
          )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* ============================================ */}
      {/* KPIs PRINCIPALES - SOLO 4 ESENCIALES */}
      {/* ============================================ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* KPI 1: Ventas Totales (Ingresos) */}
        <Card className="shadow-xl border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50 to-white hover:shadow-2xl transition-shadow">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
                  </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {totalVentas > 0 ? `$${totalVentas.toLocaleString('es-CL')}` : '—'}
            </h3>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">Ventas Totales</p>
            <p className="text-xs text-gray-500">{cantidadVentas} {cantidadVentas === 1 ? 'factura' : 'facturas'}</p>
          </div>
        </Card>

        {/* KPI 2: Egresos Totales */}
        <Card className="shadow-xl border-l-4 border-l-red-500 bg-gradient-to-br from-red-50 to-white hover:shadow-2xl transition-shadow">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
                <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              </div>
                  </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {totalEgresos > 0 ? `$${totalEgresos.toLocaleString('es-CL')}` : '—'}
            </h3>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">Egresos Totales</p>
            <p className="text-xs text-gray-500">Compras + Gastos</p>
          </div>
        </Card>

        {/* KPI 3: Balance Neto */}
        <Card className={`shadow-xl border-l-4 bg-gradient-to-br hover:shadow-2xl transition-shadow ${
          balanceNeto >= 0 
            ? 'border-l-green-500 from-green-50 to-white' 
            : 'border-l-red-500 from-red-50 to-white'
        }`}>
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className={`p-2 sm:p-3 rounded-lg ${balanceNeto >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                <TrendingUp className={`w-5 h-5 sm:w-6 sm:h-6 ${balanceNeto >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              </div>
                  </div>
            <h3 className={`text-2xl sm:text-3xl font-bold mb-2 ${balanceNeto >= 0 ? 'text-green-900' : 'text-red-900'}`}>
              {balanceNeto !== 0 ? `$${balanceNeto.toLocaleString('es-CL')}` : '—'}
            </h3>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">Balance Neto</p>
            <p className="text-xs text-gray-500">Ventas - Egresos Totales</p>
          </div>
        </Card>

        {/* KPI 4: Margen Neto (%) */}
        <Card className="shadow-xl border-l-4 border-l-purple-500 bg-gradient-to-br from-purple-50 to-white hover:shadow-2xl transition-shadow">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-purple-100 rounded-lg">
                <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
                  </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {totalVentas > 0 ? `${margenNeto.toFixed(1)}%` : '—'}
            </h3>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">Margen Neto</p>
            <p className="text-xs text-gray-500">(Balance Neto / Ventas) × 100</p>
          </div>
        </Card>
      </div>

      {/* ============================================ */}
      {/* GRÁFICO MAESTRO - ANÁLISIS FINANCIERO */}
      {/* ============================================ */}
      <Card className="mb-6 sm:mb-8 shadow-xl border border-gray-200">
        {/* Header Section */}
        <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Gráfico Maestro</h3>
              <p className="text-xs text-gray-500 mt-0.5">Situación Financiera - Período: {
                periodoFiltro === 'enero' || periodoFiltro === 'febrero' || periodoFiltro === 'marzo' || 
                periodoFiltro === 'abril' || periodoFiltro === 'mayo' || periodoFiltro === 'junio' ||
                periodoFiltro === 'julio' || periodoFiltro === 'agosto' || periodoFiltro === 'septiembre' ||
                periodoFiltro === 'octubre' || periodoFiltro === 'noviembre' || periodoFiltro === 'diciembre' 
                  ? periodoFiltro.charAt(0).toUpperCase() + periodoFiltro.slice(1) + ' ' + new Date().getFullYear() :
                periodoFiltro === 'año' ? 'Anual' :
                periodoFiltro === 'personalizado' ? 'Rango personalizado' :
                'Mensual'
              }</p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 sm:p-6">
          {/* Legend Info Bar */}
          <div className="mb-4 sm:mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="w-5 h-5 rounded bg-blue-500 shadow-sm"></div>
              <div>
                <p className="text-xs font-semibold text-blue-900">Ventas</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
              <div className="w-5 h-5 rounded bg-red-500 shadow-sm"></div>
              <div>
                <p className="text-xs font-semibold text-red-900">Egresos Totales</p>
              </div>
            </div>
          </div>

          {/* Gráfico Maestro - Area Chart Style */}
          <div className="bg-white rounded-lg border border-gray-100 p-2 sm:p-4">
              {(() => {
                const datos = datosGrafico;
                const totalVentas = datos.reduce((sum, d) => sum + d.ventas, 0);
                const totalEgresos = datos.reduce((sum, d) => sum + d.egresos, 0);
                
                // Debug: verificar datos
                console.log('📊 Datos del gráfico:', datos);
                console.log('📊 Cantidad de puntos de datos:', datos.length);
                console.log('📊 Total Ventas:', totalVentas);
                console.log('📊 Total Egresos:', totalEgresos);
                console.log('📊 Ventas por mes:', datos.map(d => ({ categoria: d.categoria, ventas: d.ventas })));
                console.log('📊 Egresos por mes:', datos.map(d => ({ categoria: d.categoria, egresos: d.egresos })));
                
                // Verificar si no hay datos en el gráfico (después de filtrar)
                if (datos.length === 0) {
                  return (
                    <div className="h-[300px] sm:h-[400px] lg:h-[500px] flex flex-col items-center justify-center bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                      <BarChart3 className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-lg font-semibold text-gray-900 mb-2">No hay datos para el período seleccionado</p>
                      <p className="text-sm text-gray-700 mb-4 text-center max-w-md">
                        No se encontraron registros de ventas, compras o gastos para el período "{periodoFiltro}".
                      </p>
                      <div className="text-xs text-gray-600 bg-gray-100 p-4 rounded-lg space-y-2 max-w-md">
                        <p className="font-semibold">Datos cargados (sin filtrar):</p>
                        <p>• Ventas: {ventas.length} registros</p>
                        <p>• Compras: {compras.length} registros</p>
                        <p>• Gastos: {gastosCajaChica.length} registros</p>
                        <p className="mt-3 font-semibold">Datos filtrados por período:</p>
                        <p>• Ventas filtradas: {ventasFiltradas.length} registros</p>
                        <p>• Compras filtradas: {comprasFiltradas.length} registros</p>
                        <p>• Gastos filtrados: {gastosFiltrados.length} registros</p>
            </div>
                    </div>
                  );
                }
                
                // Mostrar advertencia si no hay datos cargados en absoluto
                if (ventas.length === 0 && compras.length === 0 && gastosCajaChica.length === 0) {
                  return (
                    <div className="h-[300px] sm:h-[400px] lg:h-[500px] flex flex-col items-center justify-center bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <Database className="w-12 h-12 text-blue-600 mb-4" />
                      <p className="text-lg font-semibold text-blue-900 mb-2">No hay datos cargados</p>
                      <p className="text-sm text-blue-700 mb-4 text-center max-w-md">
                        Presiona el botón "Actualizar Datos" para cargar la información. Si no aparecen después de actualizar, verifica:
                      </p>
                      <div className="text-xs text-blue-800 bg-blue-100 p-4 rounded-lg space-y-2 max-w-md">
                        <p className="font-semibold">Estado de carga:</p>
                        <p>• Ventas: {ventas.length} registros</p>
                        <p>• Compras: {compras.length} registros</p>
                        <p>• Gastos: {gastosCajaChica.length} registros</p>
                        <p className="mt-3 font-semibold">Solución:</p>
                        <p>• Haz clic en "Actualizar Datos" en la parte superior</p>
                        <p>• Verifica la consola del navegador (F12) para ver errores</p>
                        <p>• Asegúrate de que el servidor Mock esté funcionando</p>
                      </div>
                      <button
                        onClick={handleSincronizarERP}
                        disabled={cargandoDatos}
                        className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {cargandoDatos ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Cargando...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4" />
                            Cargar Datos Ahora
                          </>
                        )}
                      </button>
                    </div>
                  );
                }
                
                // Mostrar advertencia si no hay ventas pero hay otros datos
                if (totalVentas === 0 && totalEgresos === 0 && datos.length > 0) {
                  // Contar ventas filtradas con y sin fecha válida
                  const ventasConFecha = ventasFiltradas.filter(v => {
                    const fecha = normalizarFecha(v.fecha, v.mes);
                    return fecha !== null;
                  }).length;
                  const ventasSinFecha = ventasFiltradas.length - ventasConFecha;
                  
                  return (
                    <div className="h-[300px] sm:h-[400px] lg:h-[500px] flex flex-col items-center justify-center bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                      <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-600 mb-4" />
                      <p className="text-base sm:text-lg font-semibold text-yellow-900 mb-2 text-center">No se encontraron datos en el gráfico</p>
                      <p className="text-xs sm:text-sm text-yellow-700 mb-4 text-center max-w-md">
                        Los datos cargados no tienen fechas válidas o no están dentro del período seleccionado.
                      </p>
                      <div className="text-xs text-yellow-800 bg-yellow-100 p-3 sm:p-4 rounded-lg space-y-2 max-w-md">
                        <p className="font-semibold">Estado de los datos (período seleccionado):</p>
                        <p>• Total de registros de ventas en período: <strong>{ventasFiltradas.length}</strong></p>
                        <p>• Ventas con fecha válida: <strong className="text-green-700">{ventasConFecha}</strong></p>
                        <p>• Ventas sin fecha válida: <strong className="text-red-700">{ventasSinFecha}</strong></p>
                        <p className="mt-3 font-semibold">Otros datos (período seleccionado):</p>
                        <p>• Compras filtradas: {comprasFiltradas.length}</p>
                        <p>• Gastos filtrados: {gastosFiltrados.length}</p>
                        {ventasSinFecha > 0 && (
                          <p className="mt-3 text-yellow-900 font-semibold">
                            💡 Solución: Verifica que los datos tengan el campo "fecha" o "mes" con valores válidos dentro del período seleccionado.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                }
                
                // Si hay datos pero el array está vacío después de procesar, mostrar mensaje
                if (datos.length === 0) {
                  return (
                    <div className="h-[300px] sm:h-[400px] lg:h-[500px] flex flex-col items-center justify-center bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                      <BarChart3 className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-4" />
                      <p className="text-base sm:text-lg font-semibold text-gray-900 mb-2 text-center">No hay datos para visualizar</p>
                      <p className="text-xs sm:text-sm text-gray-700 text-center max-w-md">
                        Los datos filtrados no pudieron ser agrupados por mes. Verifica las fechas de los registros.
                      </p>
                    </div>
                  );
                }
                
                // Gráfico de áreas superpuestas (Overlapping Area Chart)
                // Las áreas de Ventas y Egresos se superponen visualmente
                // Si hay solo un punto de datos, crear puntos adicionales para mejor visualización
                // Mantenemos los mismos valores pero con etiquetas vacías para que el área se vea completa
                const datosParaGrafico = datos.length === 1 
                  ? [
                      { ...datos[0], categoria: '', categoriaOriginal: datos[0].categoria }, // Punto inicial con mismo valor, etiqueta vacía
                      datos[0], // Punto con datos y etiqueta
                      { ...datos[0], categoria: '', categoriaOriginal: datos[0].categoria }  // Punto final con mismo valor, etiqueta vacía
                    ]
                  : datos;
                
                return (
                  <div className="w-full h-[300px] sm:h-[400px] lg:h-[500px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={datosParaGrafico}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                      <defs>
                        {/* Gradiente para Egresos Totales (rojo) */}
                        <linearGradient id="colorEgresos" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#EF4444" stopOpacity={0.7}/>
                          <stop offset="95%" stopColor="#EF4444" stopOpacity={0.2}/>
                        </linearGradient>
                        {/* Gradiente para Ventas (azul) */}
                        <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORES_UNIFICADOS.ventas} stopOpacity={0.7}/>
                          <stop offset="95%" stopColor={COLORES_UNIFICADOS.ventas} stopOpacity={0.2}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                      <XAxis 
                        dataKey="categoria" 
                        angle={datosParaGrafico.length > 3 ? -45 : 0}
                        textAnchor={datosParaGrafico.length > 3 ? "end" : "middle"}
                        height={datosParaGrafico.length > 3 ? 100 : 60}
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        stroke="#d1d5db"
                        interval={0}
                        tickFormatter={(value) => value || ''} // Ocultar etiquetas vacías
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        stroke="#d1d5db"
                        tickFormatter={(value) => {
                          if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                          if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
                          return `$${value}`;
                        }}
                        domain={['auto', 'auto']}
                        allowDataOverflow={false}
                        label={{ 
                          value: 'Montos (CLP)', 
                          angle: -90, 
                          position: 'insideLeft', 
                          style: { textAnchor: 'middle', fill: '#4b5563', fontSize: '12px', fontWeight: '600' } 
                        }}
                      />
                      
                      {/* Tooltip Completo */}
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload || payload.length === 0) return null;
                          
                          const data = payload[0]?.payload;
                          if (!data) return null;
                          
                          // Si la categoría está vacía (punto duplicado), usar la categoría original del dato
                          const categoriaMostrar = label || (data as any).categoriaOriginal || data.categoria || '';
                          
                          // Ocultar tooltip en puntos duplicados sin categoría (solo mostrar en el punto central)
                          if (!label && !data.categoria && datos.length === 1) return null;
                          
                          return (
                            <div className="bg-white border border-gray-300 rounded-lg shadow-xl p-4 min-w-[300px]">
                              <p className="font-bold text-lg text-gray-900 mb-3 border-b border-gray-200 pb-2">
                                {categoriaMostrar}
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 flex items-center gap-2">
                                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                                    Ventas:
                                  </span>
                                  <span className="font-semibold text-blue-700">
                                    ${data.ventas.toLocaleString('es-CL')}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 flex items-center gap-2">
                                    <div className="w-3 h-3 rounded bg-red-500"></div>
                                    Egresos Totales:
                                  </span>
                                  <span className="font-semibold text-red-700">
                                    ${data.egresos.toLocaleString('es-CL')}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                  <span className="text-gray-700 font-semibold flex items-center gap-2">
                                    <div className="w-3 h-3 rounded bg-green-500"></div>
                                    Balance Neto:
                                  </span>
                                  <span className={`font-bold ${data.balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    ${data.balance.toLocaleString('es-CL')}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Margen Neto:</span>
                                  <span className="font-semibold text-gray-900">
                                    {data.margenNeto.toFixed(1)}%
                                  </span>
                                </div>
                                {data.variacionVentas !== 0 && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Variación Ventas:</span>
                                    <span className={`font-semibold ${data.variacionVentas >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {data.variacionVentas >= 0 ? '+' : ''}{data.variacionVentas.toFixed(1)}%
                                    </span>
                                  </div>
                                )}
                                {data.variacionEgresos !== 0 && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Variación Egresos:</span>
                                    <span className={`font-semibold ${data.variacionEgresos <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {data.variacionEgresos >= 0 ? '+' : ''}{data.variacionEgresos.toFixed(1)}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '30px' }}
                        iconType="rect"
                        iconSize={12}
                        formatter={(value) => <span style={{ fontSize: '12px', color: '#4b5563', fontWeight: '500' }}>{value}</span>}
                      />
                      
                      {/* Gráfico de áreas superpuestas: Ventas y Egresos se superponen */}
                      {/* Egresos Totales (rojo) */}
                      <Area 
                        type="monotone"
                        dataKey="egresos" 
                        stroke="#EF4444"
                        strokeWidth={2}
                        fillOpacity={0.5}
                        fill="url(#colorEgresos)"
                        name="Egresos Totales"
                        isAnimationActive={true}
                        connectNulls={false}
                      />
                      
                      {/* Ventas (azul) - se superpone sobre los egresos */}
                      <Area 
                        type="monotone"
                        dataKey="ventas" 
                        stroke={COLORES_UNIFICADOS.ventas}
                        strokeWidth={2}
                        fillOpacity={0.5}
                        fill="url(#colorVentas)"
                        name="Ventas"
                        isAnimationActive={true}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  </div>
                );
              })()}
          </div>
        </div>
      </Card>

      {/* ============================================ */}
      {/* GRÁFICOS SECUNDARIOS - ANÁLISIS DETALLADO */}
      {/* ============================================ */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        {/* Gráfico: Compras por Proveedor (TOP 3) */}
        <Card className="shadow-xl">
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Compras por Proveedor</h3>
          </div>
            <p className="text-xs text-gray-600 mb-3 sm:mb-4">Top 3 proveedores del período seleccionado</p>
            
            {datosComprasPorProveedor.length > 0 ? (
            <div className="w-full h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                  data={datosComprasPorProveedor}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    type="number"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
                      return `$${value}`;
                    }}
                  />
                  <YAxis 
                    type="category"
                    dataKey="proveedor"
                    tick={{ fontSize: 10 }}
                    width={120}
                  />
                <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                          <p className="font-bold text-sm text-gray-900 mb-2">{data.proveedor}</p>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between gap-4">
                              <span className="text-gray-600">Total Comprado:</span>
                              <span className="font-semibold text-gray-900">
                                ${data.total.toLocaleString('es-CL')}
                              </span>
                  </div>
                            <div className="flex justify-between gap-4">
                              <span className="text-gray-600">N° Facturas:</span>
                              <span className="font-semibold text-gray-900">{data.facturas}</span>
                  </div>
                </div>
                  </div>
                      );
                    }}
                  />
                  <Bar 
                    dataKey="total" 
                    fill="#14B8A6"
                    radius={[0, 4, 4, 0]}
                  />
              </BarChart>
            </ResponsiveContainer>
            </div>
            ) : (
              <div className="h-[250px] sm:h-[300px] flex items-center justify-center text-gray-400 p-4">
                <p className="text-sm text-center">No hay datos de compras para el período seleccionado</p>
                </div>
              )}
        </div>
      </Card>
      </div>

      {/* ============================================ */}
      {/* TABLA CONSOLIDADA DE TRANSACCIONES (VERSIÓN PRO) */}
      {/* ============================================ */}
      <Card className="mb-6 sm:mb-8 shadow-xl">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Transacciones Centralizadas</h3>
            </div>
            <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-100 text-indigo-800 rounded-full font-semibold text-sm">
              {totalRegistros} registros
            </span>
          </div>
          
          {/* Tabla consolidada */}
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Tipo</th>
                  <th className="px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Fecha</th>
                  <th className="px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Origen</th>
                  <th className="px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Descripción</th>
                  <th className="px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Monto (CLP)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Compras */}
                {comprasFiltradas.slice(0, 10).map((compra, index) => (
                  <tr key={`compra-${index}`} className="hover:bg-orange-50 transition-colors">
                    <td className="px-3 sm:px-4 py-2 sm:py-3">
                      <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded-full whitespace-nowrap">
                        Compra
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                      {formatearFecha(compra.fecha, compra.mes)}
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Database className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 flex-shrink-0" />
                        <span className="whitespace-nowrap">Defontana</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 min-w-[150px]">
                      <div>
                        <p className="font-medium truncate">{compra.proveedor}</p>
                        <p className="text-xs text-gray-500 truncate">{compra.descripcion}</p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-bold text-orange-700 whitespace-nowrap">
                      ${compra.monto.toLocaleString('es-CL')}
                    </td>
                  </tr>
                ))}
                
                {/* Ventas */}
                {ventasFiltradas.slice(0, 10).map((venta, index) => (
                  <tr key={`venta-${venta.id || venta.numero_boleta || `index-${index}`}`} className="hover:bg-orange-50 transition-colors">
                    <td className="px-3 sm:px-4 py-2 sm:py-3">
                      <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded-full whitespace-nowrap">
                        Venta
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                      {formatearFecha(venta.fecha, venta.mes)}
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Database className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 flex-shrink-0" />
                        <span className="whitespace-nowrap">Defontana</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 min-w-[150px]">
                      <div>
                        <p className="font-medium truncate">{venta.cliente}</p>
                        <p className="text-xs text-gray-500 truncate">{venta.descripcion || venta.categoria || 'Sin descripción'}</p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-bold text-orange-700 whitespace-nowrap">
                      ${venta.monto.toLocaleString('es-CL')}
                    </td>
                  </tr>
                ))}
                
                {/* Gastos */}
                {gastosFiltrados.length > 0 ? (
                  gastosFiltrados.slice(0, 10).map((gasto) => (
                    <tr key={`gasto-${gasto.id}`} className="hover:bg-green-50 transition-colors">
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                          Gasto
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                        {formatearFecha(gasto.fecha)}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Activity className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                          <span className="whitespace-nowrap">Caja Chica</span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 min-w-[150px]">
                        <div>
                          <p className="font-medium truncate">{gasto.trabajador}</p>
                          <p className="text-xs text-gray-500 truncate">{gasto.categoria} - {gasto.descripcion}</p>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-bold text-green-700 whitespace-nowrap">
                        ${gasto.monto.toLocaleString('es-CL')}
                      </td>
                    </tr>
                  ))
                ) : (
                  // ✅ Mensaje específico si no hay gastos aprobados
                  gastosCajaChica.length > 0 && gastosFiltradosPorPeriodo.length > 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 text-center">
                        <div className="text-yellow-600 text-sm">
                          <AlertCircle className="w-5 h-5 mx-auto mb-2" />
                          <p>No hay gastos aprobados en el período seleccionado</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Los gastos pendientes o rechazados no se muestran en esta tabla
                          </p>
                        </div>
                      </td>
                    </tr>
                  )
                )}
                
                {/* Mensaje si no hay datos */}
                {totalRegistros === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <div className="text-gray-400">
                        <Layers className="w-12 h-12 mx-auto mb-4" />
                        <p className="text-lg font-medium">No hay datos disponibles</p>
                        <p className="text-sm">Usa el botón "Actualizar Datos" para cargar información</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-gradient-to-r from-gray-100 to-gray-50">
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-right font-bold text-gray-900">
                    Total Consolidado:
                  </td>
                  <td className="px-4 py-4 text-lg font-bold text-indigo-700">
                    ${totalGeneral.toLocaleString('es-CL')}
                  </td>
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        </div>
      </Card>

    </div>
  );
}



